import { OrderInfo, OrderItemDTO } from "../../../redux/slice/order/OrderSlice";

export type InvoiceStatusCategory = "not_created" | "unpaid" | "partial" | "paid" | "unknown";

export type FulfillmentInvoiceContext = {
  invoiceId: number | null;
  invoiceStatus: string | null;
  shippingCharges: number | null;
  loaded: boolean;
};

export const initialFulfillmentInvoiceContext: FulfillmentInvoiceContext = {
  invoiceId: null,
  invoiceStatus: null,
  shippingCharges: null,
  loaded: false,
};

export const INVOICE_STATUS_LOADING_MESSAGE =
  "Invoice status is still loading. Please wait and try again.";

export const INVOICE_STATUS_UNKNOWN_MESSAGE =
  "Unable to verify invoice status. Shipping cannot be changed until invoice status is confirmed.";

export const PAID_INVOICE_POPUP_NOTE =
  "*Invoice is already paid so the new shipping charges must be same as previous charge.";

export const UNPAID_INVOICE_POPUP_NOTE =
  "*Invoice will be updated to reflect the new shipping amount.";

export const PAID_INVOICE_CHARGE_MISMATCH_MESSAGE =
  "Invoice is already paid so the new shipping charges must be same as previous charge.";

export const FULFILLMENT_SHIPPING_CHARGE_MIN_ERROR =
  "Shipping charge must be greater than 0.";

export const FULFILLMENT_SHIPPING_CHARGE_ZERO_OR_GREATER_ERROR =
  "Shipping charge must be 0 or greater.";

export function isFulfillmentShippingChargeValid(
  charge?: number | null,
  options?: { allowFreeShipping?: boolean }
): boolean {
  if (charge == null || !Number.isFinite(charge)) return false;
  if (options?.allowFreeShipping) return charge >= 0;
  return charge > 0;
}

export function extractShippingCharge(method?: string | null): number | null {
  if (!method || typeof method !== "string") return null;
  const regex = /\(\s*\$\s*([0-9]+(?:\.[0-9]+)?)\s*\)/;
  const match = method.match(regex);
  return match ? parseFloat(match[1]) : null;
}

export function formatShippingMethodLabel(method?: string | null): string {
  if (!method?.trim()) return "—";
  return method.replace(/\s*\(\s*\$\s*[0-9]+(?:\.[0-9]+)?\s*\)\s*$/, "").trim();
}

export function formatShippingMethodWithCharge(method: string, charge: number): string {
  const base = formatShippingMethodLabel(method);
  if (!base || base === "—") return method;
  return `${base} ($${charge.toFixed(2)})`;
}

export function isSameShippingMethodSelection(
  left?: string | null,
  right?: string | null
): boolean {
  if (!left || !right) return false;
  if (left === right) return true;
  return formatShippingMethodLabel(left) === formatShippingMethodLabel(right);
}

export function findShippingMethodOption<
  T extends { label: string; value: string }
>(options: T[], method?: string | null): T | undefined {
  if (!method) return undefined;
  const exact = options.find((opt) => opt.value === method);
  if (exact) return exact;
  const base = formatShippingMethodLabel(method);
  return options.find((opt) => formatShippingMethodLabel(opt.value) === base);
}

export function resolveStoredOrderShippingCharge(
  orderDetail?: OrderInfo | null
): number | null {
  const orderInfo = orderDetail?.orderInfo;
  return (
    parseShippingChargeValue(orderInfo?.shipping_charges) ??
    parseShippingChargeValue(orderInfo?.shippingPrice) ??
    parseShippingChargeValue(orderDetail?.outsideOrderShippingPrice)
  );
}

export function resolveFulfillmentShippingDisplay(
  orderDetail: OrderInfo | null | undefined,
  configOption: { label: string; value: string }
): { displayMethod: string; displayCharge: number } {
  const configCharge = extractShippingCharge(configOption.value) ?? 0;
  const storedCharge = resolveStoredOrderShippingCharge(orderDetail);
  const orderInfoMethod = orderDetail?.orderInfo?.shippingMethod?.trim();

  let displayMethod = configOption.value;

  if (storedCharge != null && !shippingChargesEqual(storedCharge, configCharge)) {
    displayMethod = formatShippingMethodWithCharge(configOption.value, storedCharge);
  } else if (orderInfoMethod) {
    displayMethod = orderInfoMethod;
  }

  const displayCharge =
    storedCharge ??
    extractShippingCharge(displayMethod) ??
    configCharge;

  return { displayMethod, displayCharge };
}

export function formatShippingCurrency(amount?: number | string | null): string {
  if (amount == null || amount === "") return "—";
  const parsed = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(parsed)) return "—";
  return `$${parsed.toFixed(2)}`;
}

export function parseShippingChargeValue(value?: string | number | null): number | null {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isPaidInvoiceStatus(status?: string | null): boolean {
  const normalized = (status || "").trim().toUpperCase();
  return normalized === "PAID" || normalized === "CLOSED";
}

export function isPartiallyPaidInvoiceStatus(status?: string | null): boolean {
  const normalized = (status || "").trim().toUpperCase();
  return normalized === "PARTIAL";
}

export function resolveInvoiceStatusDisplay(
  status?: string | null,
  options?: { hasInvoice?: boolean }
): { label: string; category: InvoiceStatusCategory } {
  if (!status || status.trim() === "") {
    if (options?.hasInvoice) {
      return { label: "Unknown", category: "unknown" };
    }
    return { label: "Not Created", category: "not_created" };
  }

  if (isPaidInvoiceStatus(status)) {
    return { label: "Paid", category: "paid" };
  }

  if (isPartiallyPaidInvoiceStatus(status)) {
    return { label: "Partial", category: "partial" };
  }

  return { label: "UnPaid", category: "unpaid" };
}

export function resolveInvoiceIdFromOrder(
  orderDetail?: OrderInfo | null,
  orderItems?: OrderItemDTO[] | null
): number | null {
  if (orderDetail?.invoiceId) {
    return orderDetail.invoiceId;
  }
  const itemWithInvoice = orderItems?.find((item) => item.invoiceId);
  return itemWithInvoice?.invoiceId ?? null;
}

export function resolveRequestedShippingInfo(orderDetail?: OrderInfo | null): {
  method: string;
  charge: number;
} {
  if (!orderDetail) {
    return { method: "", charge: 0 };
  }

  const method =
    orderDetail.requestedShippingMethod ||
    orderDetail.orderInfo?.shippingMethod ||
    "";

  if (orderDetail.isOutsideOrder) {
    const outsideCharge =
      parseShippingChargeValue(orderDetail.outsideOrderShippingPrice) ??
      parseShippingChargeValue(orderDetail.orderInfo?.shippingPrice) ??
      parseShippingChargeValue(orderDetail.orderInfo?.shipping_charges) ??
      extractShippingCharge(method);
    return { method, charge: outsideCharge ?? 0 };
  }

  const storedCharge =
    parseShippingChargeValue(orderDetail.orderInfo?.shipping_charges) ??
    parseShippingChargeValue(orderDetail.orderInfo?.shippingPrice) ??
    extractShippingCharge(method);

  return { method, charge: storedCharge ?? 0 };
}

/** First-requested method. Charge prefers the value embedded on requestedShippingMethod so a later fulfill overwrite of shipping_charges is not shown as the original. */
export function resolveOriginalRequestedShippingInfo(orderDetail?: OrderInfo | null): {
  method: string;
  charge: number;
} {
  const requested = resolveRequestedShippingInfo(orderDetail);
  const embeddedCharge = extractShippingCharge(orderDetail?.requestedShippingMethod);
  return {
    method: requested.method,
    charge: embeddedCharge ?? requested.charge,
  };
}

export type AppliedFulfillmentShipping = {
  method?: string | null;
  charge?: number | null;
};

/** Last saved / last confirmed fulfillment method (not a full audit trail). */
export function resolveCurrentFulfillmentShippingInfo(
  orderDetail?: OrderInfo | null,
  applied?: AppliedFulfillmentShipping | null
): { method: string; charge: number } {
  const method =
    applied?.method?.trim() ||
    orderDetail?.orderInfo?.shippingMethod?.trim() ||
    orderDetail?.actualShippingMethod?.trim() ||
    "";

  const charge =
    parseShippingChargeValue(applied?.charge) ??
    parseShippingChargeValue(orderDetail?.orderInfo?.shipping_charges) ??
    parseShippingChargeValue(orderDetail?.orderInfo?.shippingPrice) ??
    extractShippingCharge(method) ??
    0;

  return { method, charge };
}

export function shouldShowCurrentShippingHistory(
  originalMethod?: string | null,
  currentMethod?: string | null
): boolean {
  if (!originalMethod?.trim() || !currentMethod?.trim()) return false;
  return !isSameShippingMethodSelection(originalMethod, currentMethod);
}

export function resolvePreviousShippingCharge(orderDetail?: OrderInfo | null): number {
  if (!orderDetail) return 0;

  const fromOrderInfo =
    parseShippingChargeValue(orderDetail.orderInfo?.shipping_charges) ??
    parseShippingChargeValue(orderDetail.orderInfo?.shippingPrice);

  if (fromOrderInfo != null) return fromOrderInfo;

  return resolveRequestedShippingInfo(orderDetail).charge;
}

export function resolvePreviousShippingChargeForFulfillment(
  orderDetail?: OrderInfo | null,
  invoiceContext?: Pick<FulfillmentInvoiceContext, "invoiceStatus" | "shippingCharges" | "invoiceId"> | null
): number {
  const hasInvoice = Boolean(invoiceContext?.invoiceId);
  const statusInfo = resolveInvoiceStatusDisplay(invoiceContext?.invoiceStatus ?? null, { hasInvoice });

  if (statusInfo.category === "paid" && invoiceContext?.shippingCharges != null) {
    return invoiceContext.shippingCharges;
  }

  return resolvePreviousShippingCharge(orderDetail);
}

export function shouldUpdateInvoiceShipping(
  invoiceStatus?: string | null,
  shippingChanged = false,
  hasInvoice = false
): boolean {
  if (!shippingChanged || !hasInvoice) return false;
  const normalized = (invoiceStatus || "").toUpperCase();
  if (!normalized || isPaidInvoiceStatus(normalized)) return false;
  return true;
}

export function getFulfillmentShippingBlockMessage(
  invoiceContext: Pick<FulfillmentInvoiceContext, "invoiceId" | "invoiceStatus" | "loaded">,
  shippingChanged: boolean
): string | null {
  if (!shippingChanged) return null;
  if (!invoiceContext.loaded) return INVOICE_STATUS_LOADING_MESSAGE;
  if (invoiceContext.invoiceId && !invoiceContext.invoiceStatus) return INVOICE_STATUS_UNKNOWN_MESSAGE;
  return null;
}

export function getPaidInvoiceChargeMismatchMessage(
  invoiceContext: Pick<FulfillmentInvoiceContext, "invoiceId" | "invoiceStatus">,
  currentCharge: number,
  previousCharge: number,
  shippingChanged: boolean
): string | null {
  if (!shippingChanged || !invoiceContext.invoiceId) return null;
  if (!isPaidInvoiceStatus(invoiceContext.invoiceStatus)) return null;
  if (shippingChargesEqual(currentCharge, previousCharge)) return null;
  return PAID_INVOICE_CHARGE_MISMATCH_MESSAGE;
}

export function shippingChargesEqual(
  left?: number | null,
  right?: number | null
): boolean {
  if (left == null || right == null) return false;
  return Math.abs(left - right) < 0.005;
}

export function isPaidInvoiceConfirmBlocked(
  previousCharge: number,
  fulfillmentShippingMethod: string,
  effectiveCharge: number
): boolean {
  if (!shippingChargesEqual(effectiveCharge, previousCharge)) {
    return true;
  }
  const selectedMethodCharge = extractShippingCharge(fulfillmentShippingMethod);
  if (selectedMethodCharge == null) {
    return false;
  }
  return !shippingChargesEqual(selectedMethodCharge, previousCharge);
}
